import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnFiltersState,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import _unescape from 'lodash/unescape';

import { EntityType } from 'shared/constants';
import useEntityDialogState from 'shared/hooks/useEntityDialogState';
import useSelector from 'shared/hooks/useSelector';
import {
  createStatusOptionFilterFn,
  createYearFilterFn,
} from 'shared/utils/grid';
import formatDatetime from 'shared/utils/formatDatetime';

import Dialog from 'shared/components/Dialog';
import Grid from 'shared/components/Grid';
import TableFilterComponent from 'shared/components/TableFilterComponent';
import AttachIcon from 'shared/icons/Attach';

import { NotificationBadge } from 'features/notification';

import type { KeyOrderCase } from '../../types';
import {
  selectFilteredBySearchTermKeyOrdersList,
  selectStatusOptions,
  selectYearOptions,
} from '../../selectors';
import KeyOrderViewForm from '../KeyOrderViewForm';
import { Status } from './KeyOrdersGrid.styled';

type KeyOrdersGridProps = {
  isFetching: boolean;
};

const columnHelper = createColumnHelper<KeyOrderCase>();

const KeyOrdersGrid = ({ isFetching }: KeyOrdersGridProps) => {
  const { t } = useTranslation();

  const entityDialogState = useEntityDialogState();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([
    { id: 'Status_Label', value: [] },
    { id: 'CreatedOn', value: [] },
  ]);
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'CreatedOn', desc: true },
  ]);

  const statusOptions = useSelector(selectStatusOptions);
  const yearOptions = useSelector(selectYearOptions);
  const keyOrdersList = useSelector(selectFilteredBySearchTermKeyOrdersList);

  const columns = useMemo(
    () => [
      columnHelper.accessor('Title', {
        header: t('#portal.incident.label.title'),
        cell: (info) => {
          const value = info.getValue();

          return (
            <NotificationBadge
              entityId={info.row.original.Id}
              entityType={EntityType.KeyOrder}
            >
              {value && _unescape(value)}
            </NotificationBadge>
          );
        },
        meta: {
          sx: { width: '20%' },
        },
      }),
      columnHelper.accessor('Space.Name', {
        header: t('##attribute#incident#uds_spaceid'),
        sortingFn: 'alphanumeric',
        meta: {
          sx: { width: '20%' },
        },
      }),
      columnHelper.accessor('Status.Label', {
        header: t('##attribute#incident#statuscode'),
        filterFn: createStatusOptionFilterFn<KeyOrderCase>(),
        cell: (info) => {
          const status = info.row.original.Status;
          if (!status) return null;

          return <Status status={status.Value}>{status.Label}</Status>;
        },
        meta: {
          sx: { width: '20%' },
          filterComponent: ({ column, table }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              showSelectAllOption
              selectAllLabel={'Kaikki vaiheet'}
              options={statusOptions}
            />
          ),
        },
      }),
      columnHelper.accessor('HasFiles', {
        enableSorting: false,
        header: t('#portal.incident.grid.label.attachments'),
        cell: (info) =>
          info.getValue() && <AttachIcon sx={{ fontSize: '1.3rem' }} />,
        meta: { align: 'center', sx: { width: '10%' } },
      }),
      columnHelper.accessor('CreatedOn', {
        header: t('##attribute#incident#createdon'),
        cell: (info) => formatDatetime(info.getValue()),
        sortingFn: 'datetime',
        filterFn: createYearFilterFn<KeyOrderCase>('CreatedOn'),
        meta: {
          sx: { width: '17.5%' },
          filterComponent: ({ column, table }) => (
            <TableFilterComponent
              column={column}
              table={table}
              selectAllByDefault
              selectAllLabel={'Vuosi'}
              options={yearOptions}
              showSelectAllOption
            />
          ),
        },
      }),
      columnHelper.accessor('ModifiedOn', {
        header: t('##attribute#incident#modifiedon'),
        cell: (info) => formatDatetime(info.getValue()),
        sortingFn: 'datetime',
        meta: {
          sx: { width: '17.5%' },
        },
      }),
    ],
    [t, statusOptions, yearOptions]
  );

  const table = useReactTable({
    data: keyOrdersList,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });

  const handleClickRow = (
    _event: MouseEvent<HTMLTableRowElement>,
    row: Row<KeyOrderCase>
  ) => {
    entityDialogState.onOpen(row.original.Id);
  };

  const handleCloseDialog = () => {
    entityDialogState.onClose();
  };

  return (
    <>
      <Grid isLoading={isFetching} table={table} onClickRow={handleClickRow} />
      <Dialog
        open={entityDialogState.open}
        maxWidth="lg"
        fullWidth
        onClose={handleCloseDialog}
        TransitionProps={{
          onExited: entityDialogState.onClearId,
        }}
      >
        <KeyOrderViewForm caseId={entityDialogState.id!} />
      </Dialog>
    </>
  );
};

export default KeyOrdersGrid;
